import type { HTMLAttributes, KeyboardEventHandler, RefObject } from 'react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Link from 'components/Link';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import Text from 'components/Text';
import { SEARCH_QUERY_PARAM } from 'constants/queryParams';
import { useGlobalLinks } from 'hooks';
import { usePopularSearchTerms } from 'hooks/data-fetching';
import { cn } from 'utils/classNames';
import { range } from 'utils/helpers';
import { createUrl } from 'utils/url';

type SearchTermsVariant = 'chips' | 'iconLinks';

interface Props extends HTMLAttributes<HTMLDivElement> {
	/** Heading text. */
	heading?: string;

	/** Only display up to this number of links. */
	itemLimit?: number;

	/** Ref for the last term link. */
	lastLinkRef?: RefObject<HTMLAnchorElement | null>;

	/** Tab key handler for the last term link. */
	onLastLinkTab?: KeyboardEventHandler<HTMLAnchorElement>;

	/** Style for the search term links. */
	variant: SearchTermsVariant;
}

interface ListProps extends Props {
	terms: string[];
}

export function PopularSearchTermsList({
	heading,
	itemLimit = 0,
	lastLinkRef,
	onLastLinkTab,
	terms,
	variant,
	...props
}: ListProps) {
	const { search } = useGlobalLinks();
	const visibleTerms = terms.slice(0, itemLimit || 999);
	const hasTerms = visibleTerms.length > 0;
	const lastTermIndex = visibleTerms.length - 1;

	const handleLastLinkKeyDown: KeyboardEventHandler<HTMLAnchorElement> = (
		e,
	) => {
		if (onLastLinkTab && e.key === 'Tab' && !e.shiftKey) {
			onLastLinkTab(e);
		}
	};

	const items = visibleTerms.map((term, i) => ({
		href: search ? createUrl(search, { [SEARCH_QUERY_PARAM]: term }) : '',
		text: term,
		ref: i === lastTermIndex ? lastLinkRef : undefined,
		onKeyDown: i === lastTermIndex ? handleLastLinkKeyDown : undefined,
	}));

	const itemMarginClasses = cn(
		'first:mt-0',
		variant === 'chips' && 'mt-r-sm',
		variant === 'iconLinks' && 'mt-6 md:mt-4',
	);

	return (
		<div {...props}>
			{heading && (
				<Text as="h3" className="mb-r-md">
					{heading}
				</Text>
			)}
			{hasTerms && (
				<ul>
					{items.map(({ href, onKeyDown, ref, text }) => (
						<li key={text} className={itemMarginClasses}>
							{variant === 'chips' && (
								<Button
									ref={ref}
									variant="secondary"
									href={href}
									onKeyDown={onKeyDown}
								>
									{text}
								</Button>
							)}
							{variant === 'iconLinks' && (
								<Link
									href={href}
									ref={ref}
									onKeyDown={onKeyDown}
									className="group/popular-term inline-flex items-center align-top"
								>
									<Icon name="search" className="mr-2" />
									<span className="group-hover/popular-term:underline">
										{text}
									</span>
								</Link>
							)}
						</li>
					))}
				</ul>
			)}
			{!hasTerms && (
				<LoadingSkeleton>
					{range(itemLimit || 5).map((i) => (
						<LoadingSkeletonItem
							key={i}
							width={`${50 + (7 % (i + 1)) * 7}px`}
							height={variant === 'chips' ? '2.5rem' : '24px'}
							className={itemMarginClasses}
						/>
					))}
				</LoadingSkeleton>
			)}
		</div>
	);
}
PopularSearchTermsList.displayName = 'PopularSearchTermsList';

/** List of X popular search terms, optinally with a heading. */
export default function PopularSearchTerms({ itemLimit, ...props }: Props) {
	const { terms } = usePopularSearchTerms({ itemLimit });

	return <PopularSearchTermsList terms={terms} {...props} />;
}
PopularSearchTerms.displayName = 'PopularSearchTerms';
